import { useExamEventDetails } from '@exam-center/exam-event-management';
import { ExamType, useSearchExamineeStatusesInExamEventQuery } from '@exam-center/graphql';
import { APP_PERMISSIONS } from '@exam-center/permission';
import { useTimeGate } from '@exam-center/server-time';
import { Flex, Loading, PaperSection, Tooltip } from '@shared/components';
import { WithPermission } from '@shared/permissions';
import { dayjs } from '@shared/utils';
import { useMemo, useState } from 'react';

import {
  GENERATE_SEAT_NUMBER_REGISTRATION_STATUSES,
  PRINTABLE_REGISTRATION_STATUSES,
} from '../../constants';
import { ConfirmGenerateSeatNumberModal } from '../confirm-generate-seat-number-modal';
import { ExamSessionExamineeSearchBox } from '../exam-session-examinee-search-box';
import { ExamineeMonitorList } from '../examinee-monitor-list';
import { ExamineeMonitorListWithoutStatuses } from '../examinee-monitor-list-without-statuses';
import { ExamineeMonitorListWithoutStatusesAndExamStatus } from '../examinee-monitor-list-without-statuses-and-exam-status';
import { ExamineeSummaryDashboard } from '../examinee-summary-dashboard';
import { GenerateSeatNumberButton } from '../generate-seat-number-button';
import { PrintAllExaminationCodesButton } from '../print-all-examination-codes-button';

export default function ExamineeMonitorListSection() {
  const {
    examEvent: {
      registrationStatus,
      id: examEventId,
      numberOfExaminees,
      sessions,
      examStartedAt,
      examType,
    },
  } = useExamEventDetails();

  const threeDaysBeforeExamGate = useMemo(
    () => dayjs(examStartedAt).startOf('day').subtract(3, 'day').toDate(),
    [examStartedAt],
  );

  const isLessThanThreeDaysBeforeExam = useTimeGate(threeDaysBeforeExamGate);

  const shouldDisplayPrintAllExaminationCode =
    PRINTABLE_REGISTRATION_STATUSES.includes(registrationStatus) &&
    numberOfExaminees &&
    examType !== ExamType.Paper;

  const shouldDisplayGenerateSeatNumberButton =
    GENERATE_SEAT_NUMBER_REGISTRATION_STATUSES.includes(registrationStatus) &&
    isLessThanThreeDaysBeforeExam;

  const [isOpenConfirmGenerateSeatNumberModal, setIsOpenConfirmGenerateSeatNumberModal] =
    useState(false);

  const handleOpenConfirmGenerateSeatNumberModal = () => {
    setIsOpenConfirmGenerateSeatNumberModal(true);
  };

  const handleCloseConfirmGenerateSeatNumberModal = () => {
    setIsOpenConfirmGenerateSeatNumberModal(false);
  };

  const { data, loading: isLoading } = useSearchExamineeStatusesInExamEventQuery({
    variables: {
      searchExamineeStatusesInExamEvent: {
        examEventId: examEventId,
        searchExaminee: {},
        paging: null,
      },
    },
  });

  const disabled = !data?.ecSearchExamineeStatusesInExamEvent.examEventExaminees.every(
    (examinee) => examinee.seatNumber === null,
  );

  const shouldDisplayWithStatus = sessions.length <= 1;
  const isPaperExam = examType === ExamType.Paper;

  if (isLoading)
    return (
      <Loading
        sx={{
          width: '100%',
        }}
      />
    );
  return (
    <PaperSection
      title='รายชื่อผู้เข้าสอบ'
      endAdornment={
        <WithPermission requiredPermission={APP_PERMISSIONS.PROCTORING.DETAILS.UPDATE}>
          <Flex sx={{ gap: 3 }}>
            {shouldDisplayGenerateSeatNumberButton ? (
              <Tooltip
                title='ไม่สามารถสร้างซ้ำได้ เนื่องจากระบบ ได้สร้างเลขที่นั่งสอบเรียบร้อยแล้ว'
                maxWidth={230}
                arrow
                placement='left'
                disableHoverListener={!disabled}
              >
                <GenerateSeatNumberButton
                  onClick={handleOpenConfirmGenerateSeatNumberModal}
                  disabled={disabled}
                />
              </Tooltip>
            ) : null}
            {shouldDisplayPrintAllExaminationCode ? (
              <PrintAllExaminationCodesButton
                shouldDisplayConfirmModal={!disabled}
                examEventId={examEventId}
              />
            ) : null}
          </Flex>
        </WithPermission>
      }
    >
      <Flex
        sx={(theme) => ({
          flexDirection: 'column',
          gap: theme.spacing(6),
          p: theme.spacing(6),
        })}
      >
        <ConfirmGenerateSeatNumberModal
          examEventId={examEventId}
          open={isOpenConfirmGenerateSeatNumberModal}
          onClose={handleCloseConfirmGenerateSeatNumberModal}
        />
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing(6),
          })}
        >
          <ExamineeSummaryDashboard />
          <ExamSessionExamineeSearchBox />
          {isPaperExam ? (
            <ExamineeMonitorListWithoutStatusesAndExamStatus />
          ) : shouldDisplayWithStatus ? (
            <ExamineeMonitorList sessionId={sessions[0].id} />
          ) : (
            <ExamineeMonitorListWithoutStatuses />
          )}
        </Flex>
      </Flex>
    </PaperSection>
  );
}
